import React, { useState, useEffect } from 'react';
import '../styles/Profile.css';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
          method: 'GET',
          credentials: 'include', // Send cookies with the request
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data);
          setPhone(data.phone || '');
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Failed to fetch profile');
          setUser(null);
        }
      } catch (error) {
        setError('Network error. Please check your connection.');
        setUser(null);
      }
    };

    fetchUser();

    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('profile-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe elements
    document.querySelectorAll('.profile-fade-in').forEach((el) => observer.observe(el));
    document.querySelectorAll('.profile-slide-up').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handlePhoneUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (phone.length < 10) {
      setError('Phone number must be at least 10 digits');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/update-phone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Send cookies with the request
        body: JSON.stringify({ phone }),
      });

      if (response.ok) {
        setSuccess('Phone number updated successfully');
        setUser({ ...user, phone });
        setIsEditing(false);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update phone number');
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    }
  };

  if (!user && error) {
    return <div className="profile-loading profile-error">{error}</div>;
  }

  if (!user) {
    return <div className="profile-loading">Loading...</div>;
  }

  return (
    <div className="profile-container">

      {/* Banner Section */}
      <section className="profile-banner-section">
        <div className="profile-banner-overlay">
          <div className="profile-banner-content">
            <h1 className="profile-banner-title profile-visible">Your Profile</h1>
            <p className="profile-banner-subtitle profile-visible">
              Manage your account details and continue your eco-friendly cycling journey with Beaver Tours
            </p>
          </div>
        </div>
      </section>

      {/* Profile Details Section */}
      <section className="profile-details-section">
        <div className="profile-wrapper">
          <div className="profile-section-header profile-fade-in profile-visible">
            <h2>Account Information</h2>
            <div className="profile-section-divider"></div>
          </div>

          <div className="profile-content profile-slide-up profile-visible">
            <div className="profile-card">
              <div className="profile-info">
                <div className="profile-field">
                  <label>Name:</label>
                  <span>{user.name}</span>
                </div>
                <div className="profile-field">
                  <label>Email:</label>
                  <span>{user.email}</span>
                </div>
                <div className="profile-field">
                  <label>Address:</label>
                  <span>{user.address}</span>
                </div>
                <div className="profile-field">
                  <label>Phone:</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Enter phone number"
                      className="profile-input"
                    />
                  ) : (
                    <span>{user.phone}</span>
                  )}
                </div>
              </div>

              <div className="profile-actions">
                {isEditing ? (
                  <>
                    <button className="profile-button profile-primary" onClick={handlePhoneUpdate}>
                      Save
                    </button>
                    <button
                      className="profile-button profile-secondary"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="profile-button profile-primary"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Phone
                  </button>
                )}
              </div>

              {error && <div className="profile-message profile-error">{error}</div>}
              {success && <div className="profile-message profile-success">{success}</div>}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Profile;